import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Camera, 
  Video, 
  MapPin, 
  X, 
  ChevronDown,
  MessageCircleHeart,
  Loader2
} from 'lucide-react';
import { useStore } from '@/store/useStore';
import { fileToBase64 } from '@/utils/fileUtils';
import type { Media, MediaType } from '@/types';

export default function AddMemory() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { memories, children, tags, addMemory, updateMemory } = useStore();
  
  const isEditing = Boolean(id);
  const existingMemory = isEditing ? memories.find(m => m.id === id) : null;
  
  const [content, setContent] = useState(existingMemory?.content || '');
  const [childId, setChildId] = useState(existingMemory?.childId || children[0]?.id || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(existingMemory?.tags || []);
  const [media, setMedia] = useState<Media[]>(existingMemory?.media || []);
  const [location, setLocation] = useState(existingMemory?.location || '');
  const [isCutePhrase, setIsCutePhrase] = useState(existingMemory?.isCutePhrase || false);
  const [showChildPicker, setShowChildPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const selectedChild = children.find(c => c.id === childId);

  const handleMediaSelect = async (e: React.ChangeEvent<HTMLInputElement>, type: MediaType) => {
    const files = e.target.files;
    if (!files) return;

    setIsSubmitting(true);
    
    try {
      const newMedia: Media[] = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const base64 = await fileToBase64(file);
        
        newMedia.push({
          id: Date.now().toString() + i,
          type,
          url: base64,
          name: file.name,
          size: file.size,
        });
      }
      
      setMedia(prev => [...prev, ...newMedia]);
    } catch (error) {
      console.error('Failed to process media:', error);
      alert('处理文件失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeMedia = (mediaId: string) => {
    setMedia(prev => prev.filter(m => m.id !== mediaId));
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleSubmit = async () => {
    if (!content.trim()) {
      alert('请输入内容');
      return;
    }

    setIsSubmitting(true);

    try {
      const memoryData = {
        content: content.trim(),
        childId,
        media,
        tags: selectedTags,
        location: location.trim() || undefined,
        isFavorite: existingMemory?.isFavorite || false,
        isCutePhrase,
      };

      if (isEditing && existingMemory) {
        updateMemory(existingMemory.id, memoryData);
      } else {
        addMemory(memoryData);
      }

      navigate(-1);
    } catch (error) {
      console.error('Failed to save memory:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 自动标记童言趣语标签
  useEffect(() => {
    const cutePhraseTag = tags.find(t => t.name === '童言趣语');
    if (isCutePhrase && cutePhraseTag && !selectedTags.includes(cutePhraseTag.id)) {
      setSelectedTags(prev => [...prev, cutePhraseTag.id]);
    }
  }, [isCutePhrase, tags, selectedTags]);

  return (
    <div className="min-h-screen bg-primary-50 pb-24">
      <div className="px-4 py-4 space-y-4">
        {/* 孩子选择 */}
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <label className="text-sm text-gray-500 mb-2 block">记录对象</label>
          <button
            onClick={() => setShowChildPicker(true)}
            className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: selectedChild?.color || '#ccc' }}
              >
                {selectedChild?.name.charAt(0) || '?'}
              </div>
              <span className="font-medium text-gray-800">{selectedChild?.name}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* 内容输入 */}
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="记录美好的瞬间..."
            className="w-full min-h-[150px] resize-none text-gray-700 placeholder-gray-400 leading-relaxed"
            autoFocus
          />
          
          {/* 童言趣语开关 */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <MessageCircleHeart className="w-5 h-5 text-baby-pink" />
              <span className="text-sm text-gray-600">这是童言趣语</span>
            </div>
            <button
              onClick={() => setIsCutePhrase(!isCutePhrase)}
              className={`w-12 h-6 rounded-full transition-colors relative ${
                isCutePhrase ? 'bg-baby-pink' : 'bg-gray-200'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                isCutePhrase ? 'translate-x-7' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* 媒体附件 */}
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <label className="text-sm text-gray-500 mb-3 block">添加照片/视频</label>
          
          {media.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-3">
              {media.map((item) => (
                <div key={item.id} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                  {item.type === 'image' ? (
                    <img src={item.url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <video src={item.url} className="w-full h-full object-cover" />
                  )}
                  <button
                    onClick={() => removeMedia(item.id)}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="flex gap-3">
            <button
              onClick={() => imageInputRef.current?.click()}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-xl text-primary-600 font-medium hover:bg-primary-100 transition-colors disabled:opacity-50"
            >
              <Camera className="w-5 h-5" />
              照片
            </button>
            <button
              onClick={() => videoInputRef.current?.click()}
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-xl text-primary-600 font-medium hover:bg-primary-100 transition-colors disabled:opacity-50"
            >
              <Video className="w-5 h-5" />
              视频
            </button>
            
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleMediaSelect(e, 'image')}
            />
            <input
              ref={videoInputRef}
              type="file"
              accept="video/*"
              multiple
              onChange={(e) => handleMediaSelect(e, 'video')}
            />
          </div>
        </div>

        {/* 标签选择 */}
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-gray-500">添加标签</label>
            {selectedTags.length > 0 && (
              <span className="text-xs text-primary-500">{selectedTags.length} 个标签</span>
            )}
          </div>
          
          <div className="flex flex-wrap gap-2">
            {tags.map(tag => (
              <button
                key={tag.id}
                onClick={() => toggleTag(tag.id)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                  selectedTags.includes(tag.id)
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={selectedTags.includes(tag.id) ? { backgroundColor: tag.color } : undefined}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>

        {/* 位置 */}
        <div className="bg-white rounded-2xl p-4 card-shadow">
          <label className="text-sm text-gray-500 mb-2 block">位置</label>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <MapPin className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="添加位置信息"
              className="flex-1 bg-transparent text-gray-700 placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 p-4 safe-bottom">
        <div className="max-w-lg mx-auto flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 py-3 bg-gray-100 rounded-xl font-medium text-gray-700 hover:bg-gray-200 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="flex-1 py-3 bg-primary-500 rounded-xl font-medium text-white hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting && <Loader2 className="w-5 h-5 animate-spin" />}
            {isEditing ? '保存修改' : '发布记录'}
          </button>
        </div>
      </div>

      {/* 孩子选择器模态框 */}
      {showChildPicker && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-end"
          onClick={() => setShowChildPicker(false)}
        >
          <div 
            className="w-full bg-white rounded-t-3xl p-4 animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">选择记录对象</h3>
              <button 
                onClick={() => setShowChildPicker(false)}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {children.map(child => (
                <button
                  key={child.id}
                  onClick={() => {
                    setChildId(child.id);
                    setShowChildPicker(false);
                  }}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl transition-colors ${
                    childId === child.id ? 'bg-primary-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: child.color }}
                  >
                    {child.name.charAt(0)}
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-800">{child.name}</p>
                    {child.birthDate && (
                      <p className="text-sm text-gray-400">
                        生日: {child.birthDate}
                      </p>
                    )}
                  </div>
                  {childId === child.id && (
                    <div className="ml-auto w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
